/**
 * Utility functions for scenario management
 */

/**
 * Get scenario ID from a scenario object, handling different field name variations
 * @param {Object} scenario - The scenario object
 * @returns {string|null} - The scenario ID or null if not found
 */
export const getScenarioId = (scenario) => {
    return scenario?.scenarioId || scenario?.scenario_id || scenario?.id || null;
};

/**
 * Get scenario description from a scenario object, handling different field name variations
 * @param {Object} scenario - The scenario object
 * @returns {string} - The scenario description or default text
 */
export const getScenarioDescription = (scenario) => {
    return scenario?.description || scenario?.name || 'No description';
};

/**
 * Get scenario sale type from a scenario object, handling different field name variations
 * @param {Object} scenario - The scenario object
 * @returns {string} - The scenario sale type or default text
 */
export const getScenarioSaleType = (scenario) => {
    return scenario?.saleType || scenario?.sale_type || 'N/A';
};

/**
 * Get scenario tax rate from a scenario object, handling different field name variations
 * @param {Object} scenario - The scenario object
 * @returns {string} - The scenario tax rate or default text
 */
export const getScenarioTaxRate = (scenario) => {
    return scenario?.rate || scenario?.taxRate || scenario?.tax_rate || 'N/A';
};

/**
 * Check if scenario has canvas configuration
 * @param {Object} scenario - The scenario object
 * @returns {boolean} - True if scenario has canvas config
 */
export const hasCanvasConfig = (scenario) => {
    return !!(scenario?.canvasConfig || scenario?.canvas_config || scenario?.templateCanvas);
};

/**
 * Remove duplicate scenarios from an array based on scenario ID
 * @param {Array} scenarios - Array of scenario objects
 * @returns {Array} - Array with duplicates removed
 */
export const removeDuplicateScenarios = (scenarios) => {
    if (!Array.isArray(scenarios)) return [];
    
    return scenarios.filter((scenario, index, self) => {
        const scenarioId = getScenarioId(scenario);
        if (!scenarioId) return false;
        
        // Find first occurrence of this scenario ID
        return index === self.findIndex(s => {
            const sId = getScenarioId(s);
            return sId === scenarioId;
        });
    });
};

/**
 * Format scenarios for dropdown display
 * @param {Array} scenarios - Array of scenario objects
 * @returns {Array} - Array of formatted options for dropdown
 */
export const formatScenariosForDropdown = (scenarios) => {
    const uniqueScenarios = removeDuplicateScenarios(scenarios);
    
    return uniqueScenarios.map((scenario, index) => {
        const scenarioId = getScenarioId(scenario);
        const description = getScenarioDescription(scenario);
        
        return {
            key: `${scenarioId}-${index}`,
            value: scenarioId,
            label: `${scenarioId} - ${description}`,
            scenario: scenario
        };
    });
};

/**
 * Find scenario by ID from an array
 * @param {Array} scenarios - Array of scenario objects
 * @param {string} scenarioId - The scenario ID to find
 * @returns {Object|null} - The found scenario or null
 */
export const findScenarioById = (scenarios, scenarioId) => {
    if (!Array.isArray(scenarios) || !scenarioId) return null;
    
    return scenarios.find(scenario => {
        const sId = getScenarioId(scenario);
        return sId === scenarioId;
    }) || null;
};

/**
 * Validate scenario object structure
 * @param {Object} scenario - The scenario object to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateScenario = (scenario) => {
    const errors = [];
    
    if (!scenario) {
        errors.push('Scenario object is required');
        return { isValid: false, errors };
    }
    
    const scenarioId = getScenarioId(scenario);
    if (!scenarioId) {
        errors.push('Scenario ID is required');
    }
    
    const description = getScenarioDescription(scenario);
    if (description === 'No description') {
        errors.push('Scenario description is missing');
    }
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Debug scenario array for duplicates and structure issues
 * @param {Array} scenarios - Array of scenario objects
 * @param {string} context - Context string for logging
 */
export const debugScenarios = (scenarios, context = 'Unknown') => {
    if (!Array.isArray(scenarios)) {
        console.warn(`🐛 [${context}] Scenarios is not an array:`, scenarios);
        return;
    }
    
    console.log(`🔍 [${context}] Debugging ${scenarios.length} scenarios`);
    
    // Check for duplicates
    const scenarioIds = scenarios.map(s => getScenarioId(s)).filter(Boolean);
    const uniqueIds = [...new Set(scenarioIds)];
    
    if (scenarioIds.length !== uniqueIds.length) {
        const duplicates = scenarioIds.filter((id, index) => scenarioIds.indexOf(id) !== index);
        console.warn(`⚠️ [${context}] Found ${scenarioIds.length - uniqueIds.length} duplicates:`, duplicates);
    }
    
    // Check for missing IDs
    const missingIds = scenarios.filter(s => !getScenarioId(s));
    if (missingIds.length > 0) {
        console.warn(`⚠️ [${context}] Found ${missingIds.length} scenarios without IDs:`, missingIds);
    }
    
    // Sample structure check
    if (scenarios.length > 0) {
        const sample = scenarios[0];
        console.log(`📋 [${context}] Sample scenario structure:`, {
            scenarioId: sample.scenarioId,
            scenario_id: sample.scenario_id,
            id: sample.id,
            description: sample.description,
            name: sample.name,
            saleType: sample.saleType,
            sale_type: sample.sale_type,
            hasCanvas: hasCanvasConfig(sample)
        });
    }
};